import React from "react";
import { Title } from "../../../common/typography/Title";
import { useSplitOrderResources } from "../hooks/useSplitOrderResources";
import { SubTitle } from "../../../common/typography/SubTitle";
import {
  BREAKPOINT_RESERVATION_PAGE,
  Section,
} from "../../reservation-shared-styles";
import styled from "styled-components";
import { borders, colors, scrollbars } from "../../../components/common/mixins";
import Button from "../../../components/common/Button";
import {
  ItemRow,
  ItemContent,
  ItemTitleWrapper,
  ItemTitle,
  TicketSection,
  TicketNameWithDiscount,
  ItemPrice,
} from "../../../components/reservation-page/order-info";
import { formatPrice } from "../../../utils/Utils";
import { useReservationStore } from "../../../stores/useReservationStore";
import { Ticket } from "../../../types/reservation";
import { DsCheckbox } from "../../../common/inputs/DsCheckbox";
import { Divider } from "../../../common";
import WarningInfo from "../../../components/order-details/WarningInfo";

const TicketItemRow = styled(ItemRow)`
  align-items: center;
`;

const TicketWrap = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 16px;

  border: 1px solid ${colors.lightGray};
  border-radius: 8px;
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    padding-top: 12px;
  }
`;

const TicketListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px 16px;
  max-height: 496px;
  overflow-y: auto;
  ${scrollbars.thin()}
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    padding: 0 8px 12px 8px;
  }
`;

const TicketList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TicketCheckboxWrap = styled.div`
  flex: 1;
  min-width: 0;

  .MuiFormControlLabel-root {
    gap: 12px;
    width: 100%;
  }
  .MuiFormControlLabel-label {
    padding: 0;
    min-width: 0;
    overflow: hidden;
  }
`;

const SelectAllButtonWrap = styled.div`
  button > div {
    font-size: 16px;
  }
  padding: 0 16px 16px 16px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    padding: 0 8px 12px 8px;
  }
`;

interface TicketSelectionProps {
  tickets: Ticket[];
  selected: Set<number>;
  onSelectionChange: (selected: Set<number>) => void;
  isSeasonTicket?: boolean;
  error?: boolean;
}

export function TicketSelection({
  tickets,
  selected,
  onSelectionChange,
  isSeasonTicket,
  error,
}: TicketSelectionProps) {
  const r = useSplitOrderResources();
  const { currency } = useReservationStore();

  const allSelected = tickets.length > 0 && selected.size === tickets.length;

  const toggleAll = () => {
    onSelectionChange(
      allSelected ? new Set() : new Set(tickets.map((t) => t.id)),
    );
  };

  const toggleOne = (id: number, checked: boolean) => {
    const next = new Set(selected);
    checked ? next.add(id) : next.delete(id);
    onSelectionChange(next);
  };

  return (
    <Section id="ticket-selection-section">
      <div>
        <Title>{isSeasonTicket ? r.RenewTickets : r.IssueTicketsTitle}</Title>
        <SubTitle>
          {isSeasonTicket ? r.RenewTicketsSubTitle : r.IssueTicketsText}
        </SubTitle>
      </div>
      <TicketWrap>
        {error && (
          <SelectAllButtonWrap>
            <WarningInfo isError message={r.SelectTicketsRequired} />
          </SelectAllButtonWrap>
        )}
        {tickets.length > 1 && (
          <SelectAllButtonWrap>
            <Button
              variant="tertiary"
              type="button"
              text={allSelected ? r.DeselectAll : r.SelectAll}
              onClick={toggleAll}
              id="select-btn"
            />
          </SelectAllButtonWrap>
        )}
        <TicketListWrap>
          <TicketList id="ticket-selection-list">
            {tickets.map((ticket, index) => (
              <React.Fragment key={ticket.id}>
                <TicketItemRow>
                  <TicketCheckboxWrap>
                    <DsCheckbox
                      id={`ticket-${ticket.id}`}
                      label={
                        <>
                          <ItemContent>
                            {ticket.discount.name ? (
                              <TicketNameWithDiscount
                                name={ticket.priceListItem?.name ?? "Ticket"}
                                discountName={ticket.discount.name}
                              />
                            ) : (
                              <ItemTitleWrapper>
                                <ItemTitle>
                                  {ticket.priceListItem?.name ?? "Ticket"}
                                </ItemTitle>
                              </ItemTitleWrapper>
                            )}
                          </ItemContent>

                          {ticket.seat?.isNumbered && (
                            <TicketSection>
                              Section {ticket.seat.section} - {ticket.seat.row}/
                              {ticket.seat.number}
                            </TicketSection>
                          )}
                        </>
                      }
                      checked={selected.has(ticket.id)}
                      onChange={(checked) => toggleOne(ticket.id, checked)}
                      required={false}
                    />
                  </TicketCheckboxWrap>

                  <ItemPrice>
                    {formatPrice(ticket.price ?? 0, currency)}
                  </ItemPrice>
                </TicketItemRow>

                {index < tickets.length - 1 && <Divider $margin="12px 0" />}
              </React.Fragment>
            ))}
          </TicketList>
        </TicketListWrap>
      </TicketWrap>
    </Section>
  );
}
